import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { colors, font } from '../../design/tokens'
import logoLigier from '../../assets/logo_ligier.png'
import { AccesoMovilProvider, useAccesoMovil } from './accesoMovil'

// Shell mobile-first de la versión móvil de LGR (/m): ventas (crear y
// procesar NV), clientes (cta cte + deudores), logística y reparto desde el
// celular. Mismo login y permisos que la app grande (los endpoints son los
// mismos). Patrón hermano de BiAppLayout (/panel), con barra de tabs abajo.

const TABS = [
  { to: '/m',           icono: '⌂',  label: 'Inicio',    match: (p) => p === '/m' || p === '/m/' },
  { to: '/m/ventas',    icono: '🧾', label: 'Ventas',    match: (p) => p.startsWith('/m/ventas') },
  { to: '/m/clientes',  icono: '👥', label: 'Clientes',  match: (p) => p.startsWith('/m/clientes') || p.startsWith('/m/deudores') },
  { to: '/m/logistica', icono: '📦', label: 'Logística', match: (p) => p.startsWith('/m/logistica') },
  { to: '/m/reparto',   icono: '🚚', label: 'Reparto',   match: (p) => p.startsWith('/m/reparto') },
]

// Rutas raíz de cada tab: desde ellas el ← vuelve al inicio; desde cualquier
// pantalla más profunda (detalle, nueva, procesar, historia) vuelve atrás.
const RAICES = ['/m', '/m/', '/m/ventas', '/m/clientes', '/m/deudores', '/m/logistica', '/m/reparto', '/m/whatsapp']
// Secciones que no tienen pestaña pero sí título en la cabecera.
const EXTRA = [
  { match: (p) => p.startsWith('/m/whatsapp'), label: 'WhatsApp' },
  { match: (p) => p.startsWith('/m/reportes'), label: 'Reportes' },
  { match: (p) => p.startsWith('/m/consultas-stock'), label: 'Depósito' },
]

export default function MovilLayout({ children }) {
  return <AccesoMovilProvider><MovilShell>{children}</MovilShell></AccesoMovilProvider>
}

function MovilShell({ children }) {
  const { user, logout } = useAuth()
  const { cargado, puede } = useAccesoMovil()

  // "Agregar a inicio" en el iPhone usa el start_url del manifest, no la
  // página desde la que se agrega (Martin, 26 sep 2026: "agregar a inicio no
  // toma el /m"). Mientras la versión móvil está en pantalla, el manifest es
  // el de /m (abre en /m, a pantalla completa); al salir vuelve el general.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]')
    const original = link?.getAttribute('href')
    if (link) link.setAttribute('href', '/manifest-m.json')
    let meta = document.querySelector('meta[name="apple-mobile-web-app-capable"]')
    const metaNueva = !meta
    if (!meta) { meta = document.createElement('meta'); meta.name = 'apple-mobile-web-app-capable'; document.head.appendChild(meta) }
    meta.content = 'yes'
    return () => {
      if (link && original) link.setAttribute('href', original)
      if (metaNueva) meta.remove()
    }
  }, [])
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const enHome = pathname === '/m' || pathname === '/m/'
  const esDetalle = !RAICES.includes(pathname.replace(/\/$/, '') || '/m')
  // Título del header = la sección activa (en el inicio va el wordmark LGR)
  const seccion = TABS.slice(1).find((t) => t.match(pathname)) || EXTRA.find((t) => t.match(pathname))
  // La bandeja de WhatsApp maneja su propio alto y scroll: va de borde a borde.
  const sinPadding = pathname.startsWith('/m/whatsapp')
  // Pestañas = solo las secciones que el menú le da a esta persona (Inicio siempre).
  const tabs = TABS.filter((t) => t.to === '/m' || puede(t.to))
  const contenido = !cargado
    ? null
    : puede(pathname) ? children : <SinAcceso onInicio={() => navigate('/m')} />

  return (
    <div style={wrap}>
      <header style={header}>
        {enHome ? (
          <img src={logoLigier} alt="Ligier" style={{ height: 24 }} />
        ) : (
          <button onClick={() => (esDetalle ? navigate(-1) : navigate('/m'))} style={iconBtn} aria-label="Volver">←</button>
        )}
        <span style={{ fontFamily: font.sans, fontWeight: 700, fontSize: 15, letterSpacing: '0.04em', color: colors.text, textAlign: 'center' }}>
          {enHome || !seccion
            ? <>LGR <span style={{ color: colors.textMeta, fontWeight: 500 }}>móvil</span></>
            : seccion.label}
        </span>
        <button onClick={logout} style={iconBtn} aria-label="Salir" title={user?.nombre || user?.email}>⏻</button>
      </header>

      <main style={sinPadding && puede(pathname) ? { ...main, padding: 0, maxWidth: 'none' } : main}>{contenido}</main>

      <nav style={tabbar}>
        <div style={{ ...tabbarInner, gridTemplateColumns: `repeat(${tabs.length}, 1fr)` }}>
          {tabs.map((t) => {
            const on = t.match(pathname)
            return (
              <button key={t.to} onClick={() => navigate(t.to)} style={{ ...tabBtn, color: on ? colors.link : colors.textSec }}>
                <span style={{
                  fontSize: 18, lineHeight: 1, padding: '3px 12px', borderRadius: 999,
                  background: on ? colors.linkBg : 'transparent',
                }}>{t.icono}</span>
                <span style={{ fontSize: 10, fontWeight: on ? 700 : 500 }}>{t.label}</span>
              </button>
            )
          })}
        </div>
      </nav>
    </div>
  )
}

function SinAcceso({ onInicio }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 16px', fontFamily: font.sans }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
      <h2 style={{ margin: '0 0 6px', fontSize: 17, color: colors.text }}>No tenés acceso a esta pantalla</h2>
      <p style={{ margin: '0 0 18px', fontSize: 13, color: colors.textSec }}>Si la necesitás, pedile a un administrador que te la habilite en el menú.</p>
      <button onClick={onInicio} style={{ padding: '10px 18px', borderRadius: 12, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontWeight: 600, fontSize: 14, fontFamily: font.sans, cursor: 'pointer' }}>Volver al inicio</button>
    </div>
  )
}

const wrap = { minHeight: '100dvh', background: colors.bgSoft, display: 'flex', flexDirection: 'column' }
const header = {
  position: 'sticky', top: 0, zIndex: 10, background: colors.bg,
  borderBottom: `1px solid ${colors.border}`, height: 52,
  display: 'grid', gridTemplateColumns: '40px 1fr 40px', alignItems: 'center',
  padding: '0 12px', maxWidth: 560, width: '100%', margin: '0 auto', boxSizing: 'border-box',
}
const main = {
  flex: 1, width: '100%', maxWidth: 560, margin: '0 auto',
  padding: '12px 12px calc(76px + env(safe-area-inset-bottom))', boxSizing: 'border-box',
}
const tabbar = {
  position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10,
  background: colors.bg, borderTop: `1px solid ${colors.border}`,
  paddingBottom: 'env(safe-area-inset-bottom)',
}
const tabbarInner = {
  maxWidth: 560, margin: '0 auto', display: 'grid', height: 58,
}
const tabBtn = {
  border: 'none', background: 'transparent', cursor: 'pointer',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: 3, fontFamily: font.sans, padding: 0,
}
// Target táctil cómodo (~44px) para ← y ⏻
const iconBtn = {
  border: 'none', background: 'transparent', fontSize: 18, color: colors.textSec,
  cursor: 'pointer', width: 40, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
}
